import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { DateRangePicker, PickerHandle } from 'rsuite';
import type { RangeType } from 'rsuite/cjs/DateRangePicker/types';
import { SelectedBoxType } from 'src/features/trends/components/timeframe-bar-options/types';
import { useTheme, GlobalStyles } from '@mui/material';
import 'rsuite/dist/rsuite-no-reset.min.css';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { OneMonthRangeIcon } from 'src/assets/icons/OneMonthRangeIcon';

dayjs.extend(utc);
dayjs.extend(timezone);

type ShortcutLabel = Exclude<SelectedBoxType, 'custom'>;

const SHORTCUT_RANGES: Record<ShortcutLabel, () => [Date, Date]> = {
  '5m': () => [dayjs().subtract(5, 'minute').toDate(), dayjs().toDate()],
  '1h': () => [dayjs().subtract(1, 'hour').toDate(), dayjs().toDate()],
  '1d': () => [dayjs().subtract(1, 'day').toDate(), dayjs().toDate()],
  '7d': () => [dayjs().subtract(7, 'day').toDate(), dayjs().toDate()],
  '30d': () => [dayjs().subtract(30, 'day').toDate(), dayjs().toDate()],
};

const SHORTCUT_LABELS = Object.keys(SHORTCUT_RANGES) as ShortcutLabel[];

type Props = {
  onChangeTimestapHandler: (values: [Date, Date]) => void;
  onSelectBoxHandler: (value: SelectedBoxType) => void;
  timestamps: { fromDate: string; toDate: string };
  changeTimeFormatHandler: (fromDate: Date, toDate: Date) => void;
  selectedBox: SelectedBoxType;
};

export const DateRangeComponent = ({
  onChangeTimestapHandler,
  onSelectBoxHandler,
  timestamps,
  changeTimeFormatHandler,
  selectedBox,
}: Props) => {
  const [values, setValues] = useState<[Date, Date] | null>(null);
  const valuesRef = useRef<[Date, Date] | null>(null);
  const pickerRef = useRef<PickerHandle>(null);
  const theme = useTheme();

  useEffect(() => {
    const initialValues: [Date, Date] = [
      new Date(timestamps.fromDate),
      new Date(timestamps.toDate),
    ];
    setValues(initialValues);
  }, [timestamps]);

  useEffect(() => {
    const cssVariables = {
      '--rs-btn-primary-bg': theme.palette.primary.main,
      '--rs-btn-primary-hover-bg': theme.palette.primary.main,
      '--rs-btn-primary-active-bg': theme.palette.primary.main,
      '--rs-bg-active': theme.palette.primary.main,
      '--rs-calendar-range-bg': theme.palette.primary.lighter,
      '--rs-state-hover-bg': theme.palette.primary.lighter,
      '--rs-calendar-cell-selected-hover-bg': theme.palette.primary.main,
      '--rs-listbox-option-hover-text': theme.palette.primary.main,
      '--rs-listbox-option-hover-bg': theme.palette.primary.lighter,
      '--rs-input-focus-border': theme.palette.primary.main,
      '--rs-color-focus-ring': theme.palette.primary.lighter,
    };
    Object.entries(cssVariables).forEach(([variable, color]) => {
      document.documentElement.style.setProperty(variable, color);
    });
  }, [theme]);

  const rangeOptions: RangeType[] = useMemo(
    () =>
      SHORTCUT_LABELS.map((label) => ({
        label: (
          <span
            data-range-label={label}
            style={{
              display: 'inline-block',
              padding: '1px 4px',
              borderRadius: '4px',
              backgroundColor: selectedBox === label ? theme.palette.grey[300] : 'transparent',
              fontWeight: selectedBox === label ? 600 : 400,
            }}
          >
            {label}
          </span>
        ),
        value: SHORTCUT_RANGES[label],
        closeOverlay: true,
      })),
    [selectedBox, theme.palette.grey]
  );

  const onChangeHandler = (newValues: [Date, Date] | null) => {
    setValues(newValues);
    valuesRef.current = newValues;
  };

  const onApplyHandler = useCallback(() => {
    if (valuesRef.current) {
      const fromDate = new Date(valuesRef.current[0]);
      const toDate = new Date(valuesRef.current[1]);
      const localEndOfDay = dayjs.utc(valuesRef.current[1]).tz(dayjs.tz.guess()).endOf('day');
      const localEndOfDayDate = localEndOfDay.toDate();

      onChangeTimestapHandler([fromDate, toDate]);
      onSelectBoxHandler('custom');
      changeTimeFormatHandler(fromDate, localEndOfDayDate);
    }
  }, [onChangeTimestapHandler, onSelectBoxHandler, changeTimeFormatHandler]);

  const onShortcutClickHandler = useCallback(
    (range: RangeType) => {
      const label = (range.label as React.ReactElement)?.props?.[
        'data-range-label'
      ] as ShortcutLabel;
      if (!label) return;

      const [fromDate, toDate] = SHORTCUT_RANGES[label]();
      const localEndOfDay = dayjs.utc(toDate).tz(dayjs.tz.guess()).endOf('day');

      setValues([fromDate, toDate]);
      valuesRef.current = [fromDate, toDate];

      onChangeTimestapHandler([fromDate, toDate]);
      onSelectBoxHandler(label);
      changeTimeFormatHandler(fromDate, localEndOfDay.toDate());
    },
    [onChangeTimestapHandler, onSelectBoxHandler, changeTimeFormatHandler]
  );

  return (
    <>
      <GlobalStyles
        styles={{
          '.custom-date-range-picker-popup .rs-picker-toolbar-ranges .rs-btn': {
            color: 'black !important',
          },
          '.custom-date-range-picker-popup .rs-picker-toolbar-ranges .rs-btn:hover': {
            backgroundColor: '#f0f0f0 !important',
            textDecoration: 'none !important',
          },
          '.custom-date-range-picker-popup .rs-picker-toolbar-ranges .rs-btn:active': {
            backgroundColor: '#f0f0f0 !important',
            textDecoration: 'none !important',
          },
          '.custom-date-range-picker-popup .rs-picker-toolbar-ranges .rs-btn:focus': {
            backgroundColor: '#f0f0f0 !important',
            textDecoration: 'none !important',
          },
          '.custom-date-range-picker-popup .rs-picker-toolbar-ranges .rs-btn:focus-visible': {
            backgroundColor: '#f0f0f0 !important',
            textDecoration: 'none !important',
          },
        }}
      />
      <DateRangePicker
        ref={pickerRef}
        className="custom-date-range-picker"
        menuClassName="custom-date-range-picker-popup"
        value={values}
        onChange={onChangeHandler}
        onOk={onApplyHandler}
        onShortcutClick={onShortcutClickHandler}
        ranges={rangeOptions}
        caretAs={OneMonthRangeIcon}
        size="lg"
        locale={{ ok: 'Apply' }}
      />
    </>
  );
};
